import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { FIND_RIDE_COLORS } from "../constants/findRide.constants";
import { tFindRide } from "../locales/FindRideTranslations";
import { findRideStyles } from "../styles/findRide.styles";
import type { FindRideOutcome } from "../types/findRide.types";
import {
  Car,
  CheckCircle2,
  Heart,
  RefreshCw,
  ShieldCheck,
  UserCheck,
} from "lucide-react";

type FindRideOutcomeModalProps = {
  outcome: FindRideOutcome;
  /** When true, login sits on top — release Dialog focus lock so auth is usable. */
  authOverlayOpen?: boolean;
  isRefreshing?: boolean;
  notifySaved?: boolean;
  isSavingNotify?: boolean;
  notifyError?: string | null;
  onOpenChange: (open: boolean) => void;
  onRefresh: () => void;
  onSaveNotify: () => void;
  onViewAndBook: () => void;
};

function FeatureStrip() {
  return (
    <div className="border-t border-gray-100 bg-gray-50 px-6 py-4">
      <ul className="grid grid-cols-3 gap-2 text-center text-xs text-gray-600">
        <li className="flex flex-col items-center gap-1">
          <ShieldCheck
            className="h-4 w-4"
            style={{ color: FIND_RIDE_COLORS.accentGold }}
          />
          {tFindRide("featureSafeRides")}
        </li>
        <li className="flex flex-col items-center gap-1">
          <UserCheck
            className="h-4 w-4"
            style={{ color: FIND_RIDE_COLORS.accentGold }}
          />
          {tFindRide("featureVerifiedUsers")}
        </li>
        <li className="flex flex-col items-center gap-1">
          <Heart
            className="h-4 w-4"
            style={{ color: FIND_RIDE_COLORS.accentGold }}
          />
          {tFindRide("featureBestMatches")}
        </li>
      </ul>
    </div>
  );
}

/**
 * Popup for search summary: no rides / notify saved / N rides available.
 */
export function FindRideOutcomeModal({
  outcome,
  authOverlayOpen = false,
  isRefreshing = false,
  notifySaved = false,
  isSavingNotify = false,
  notifyError = null,
  onOpenChange,
  onRefresh,
  onSaveNotify,
  onViewAndBook,
}: FindRideOutcomeModalProps) {
  const open =
    outcome.kind === "no-rides" || outcome.kind === "rides-available";

  return (
    <Dialog open={open} modal={!authOverlayOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "max-w-md gap-0 overflow-hidden p-0 sm:rounded-2xl",
          authOverlayOpen && "pointer-events-none"
        )}
        onPointerDownOutside={(e) => {
          if (authOverlayOpen) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (authOverlayOpen) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (authOverlayOpen) e.preventDefault();
        }}
      >
        {outcome.kind === "no-rides" && notifySaved && (
          <>
            <DialogHeader className="space-y-4 px-6 py-8 text-center sm:text-center">
              <div
                className="mx-auto flex h-16 w-16 items-center justify-center rounded-full"
                style={{ backgroundColor: "#E8F5EE" }}
                aria-hidden
              >
                <CheckCircle2
                  className="h-9 w-9"
                  style={{ color: FIND_RIDE_COLORS.pageGreen }}
                />
              </div>
              <div className="space-y-3">
                <DialogTitle
                  className="text-2xl font-bold"
                  style={{ color: FIND_RIDE_COLORS.textPrimary }}
                >
                  {tFindRide("saveNotifyTitle")}
                </DialogTitle>
                <DialogDescription
                  className="text-sm leading-relaxed text-gray-600"
                  role="status"
                >
                  {tFindRide("saveNotifySuccess")}
                </DialogDescription>
              </div>
            </DialogHeader>

            <div className="px-6 pb-6">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className={findRideStyles.primaryCta}
                style={{ backgroundColor: FIND_RIDE_COLORS.ctaGreen }}
              >
                {tFindRide("saveNotifyDone")}
              </button>
            </div>
          </>
        )}

        {outcome.kind === "no-rides" && !notifySaved && (
          <>
            <DialogHeader className="space-y-2 border-b border-gray-100 px-6 py-5 text-left">
              <DialogTitle className="text-sm font-medium text-gray-500">
                {tFindRide("noRidesTitle")}
              </DialogTitle>
              <p
                className="text-2xl font-bold"
                style={{ color: FIND_RIDE_COLORS.textPrimary }}
              >
                <span aria-hidden style={{ color: FIND_RIDE_COLORS.accentGold }}>
                  ☹️{" "}
                </span>
                {tFindRide("noRidesOops")}
              </p>
              <DialogDescription className="text-sm leading-relaxed text-gray-600">
                {tFindRide("noRidesBody")}
              </DialogDescription>
            </DialogHeader>

            <div className="flex justify-end px-6 pt-3">
              <button
                type="button"
                onClick={onRefresh}
                disabled={isRefreshing}
                className="inline-flex items-center gap-1.5 text-sm font-semibold disabled:opacity-60"
                style={{ color: FIND_RIDE_COLORS.refreshGreen }}
              >
                <RefreshCw
                  className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
                />
                {tFindRide("refresh")}
              </button>
            </div>

            <div className="space-y-3 px-6 py-4">
              <button
                type="button"
                onClick={onSaveNotify}
                disabled={isSavingNotify}
                className={`${findRideStyles.goldOutlineBtn} disabled:cursor-not-allowed disabled:opacity-60`}
                style={{
                  borderColor: FIND_RIDE_COLORS.accentGold,
                  color: FIND_RIDE_COLORS.accentGold,
                }}
              >
                {isSavingNotify
                  ? tFindRide("saveNotifySaving")
                  : tFindRide("saveNotifyCta")}
              </button>
              {notifyError ? (
                <p className="text-center text-sm text-red-600" role="alert">
                  {notifyError}
                </p>
              ) : null}
            </div>

            <FeatureStrip />
          </>
        )}

        {outcome.kind === "rides-available" && (
          <>
            <DialogHeader className="space-y-3 px-6 py-6 text-left">
              <div className="flex items-start gap-4">
                <div
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl"
                  style={{
                    backgroundColor: "#E8F5EE",
                    color: FIND_RIDE_COLORS.pageGreen,
                  }}
                >
                  <Car className="h-6 w-6" />
                </div>
                <div>
                  <DialogTitle
                    className="text-2xl font-bold"
                    style={{ color: FIND_RIDE_COLORS.textPrimary }}
                  >
                    {tFindRide("ridesAvailableTitle", {
                      count: outcome.totalRides,
                    })}
                  </DialogTitle>
                  <DialogDescription className="mt-2 text-sm leading-relaxed text-gray-600">
                    {tFindRide("ridesAvailableBody")}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="px-6 pb-6">
              <button
                type="button"
                onClick={onViewAndBook}
                className={findRideStyles.primaryCta}
                style={{ backgroundColor: FIND_RIDE_COLORS.ctaGreen }}
              >
                {tFindRide("viewAndBookCta")}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
